import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Clock, CreditCard, Flame, Sparkles } from 'lucide-react';
import { CtaButton } from './Shared';
// Returns seconds remaining until the next midnight (end of day, local time)
const getSecondsUntilMidnight = () => {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return Math.max(0, Math.floor((midnight.getTime() - now.getTime()) / 1000));
};
export function Pricing() {
  const [timeLeft, setTimeLeft] = useState(getSecondsUntilMidnight());
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getSecondsUntilMidnight());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor(totalSeconds % 3600 / 60);
    const s = totalSeconds % 60;
    return {
      hours: h.toString().padStart(2, '0'),
      mins: m.toString().padStart(2, '0'),
      secs: s.toString().padStart(2, '0')
    };
  };
  const {
    hours,
    mins,
    secs
  } = formatTime(timeLeft);
  return <section className="bg-navy relative overflow-hidden min-h-screen flex items-center py-12" data-id="element-107">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" data-id="element-108"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-109"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-110">
        {/* Pre-headline (above title) */}
        <div className="text-center mb-4" data-id="element-111">
          <p className="text-gold font-semibold tracking-wide text-sm md:text-base" data-id="element-112">
            Un solo pago. Tuyo para siempre.
          </p>
          <p className="text-indigo-200 text-sm max-w-xl mx-auto mt-1" data-id="element-113">
            Dejá de abordar el insomnio con recomendaciones sueltas y empezá a
            trabajar con un sistema práctico diseñado para psicólogos.
          </p>
        </div>

        {/* Pricing Card — scroll target */}
        <motion.div id="comprar" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-3xl shadow-2xl overflow-hidden ring-4 ring-gold/30 scroll-mt-6" data-id="element-114">
          {/* Timer Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-500 p-3.5 text-center text-white" data-id="element-115">
            <p className="font-bold text-xs md:text-sm uppercase tracking-wider mb-1.5 flex items-center justify-center gap-1.5" data-id="element-116">
              <Flame className="w-4 h-4" data-id="element-117" />
              OFERTA DISPONIBLE POR TIEMPO LIMITADO
            </p>
            <p className="text-red-50 text-xs mb-2" data-id="element-118">
              La promoción finaliza en:
            </p>
            <div className="flex items-center justify-center gap-2 font-mono" data-id="element-119">
              <div className="bg-white/95 text-red-600 px-3 py-1.5 rounded-lg shadow-sm min-w-[48px]" data-id="element-120">
                <div className="text-xl md:text-2xl font-bold leading-none" data-id="element-121">
                  {hours}
                </div>
                <div className="text-[8px] text-red-400 uppercase font-semibold mt-0.5" data-id="element-122">
                  Horas
                </div>
              </div>
              <span className="text-xl font-bold" data-id="element-123">:</span>
              <div className="bg-white/95 text-red-600 px-3 py-1.5 rounded-lg shadow-sm min-w-[48px]" data-id="element-124">
                <div className="text-xl md:text-2xl font-bold leading-none" data-id="element-125">
                  {mins}
                </div>
                <div className="text-[8px] text-red-400 uppercase font-semibold mt-0.5" data-id="element-126">
                  Minutos
                </div>
              </div>
              <span className="text-xl font-bold" data-id="element-127">:</span>
              <div className="bg-white/95 text-red-600 px-3 py-1.5 rounded-lg shadow-sm min-w-[48px]" data-id="element-128">
                <div className="text-xl md:text-2xl font-bold leading-none" data-id="element-129">
                  {secs}
                </div>
                <div className="text-[8px] text-red-400 uppercase font-semibold mt-0.5" data-id="element-130">
                  Segundos
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8" data-id="element-131">
            <div className="text-center mb-5" data-id="element-132">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-gold text-xs font-bold mb-2 uppercase tracking-wider" data-id="element-133">
                <Sparkles className="w-3.5 h-3.5" data-id="element-134" />
                Oferta especial
              </div>
              <h3 className="font-serif text-xl md:text-2xl font-bold text-navy mb-3" data-id="element-135">
                KIT Psicología del Insomnio
              </h3>
              <div className="flex flex-col items-center justify-center" data-id="element-136">
                <span className="text-slate-400 line-through text-lg md:text-xl font-medium" data-id="element-137">
                  $49.990 ARS
                </span>
                <div className="flex items-start justify-center gap-1 mt-1" data-id="element-138">
                  <span className="text-2xl font-bold text-navy mt-1" data-id="element-139">$</span>
                  <span className="text-5xl md:text-6xl font-black text-navy tracking-tight leading-none" data-id="element-140">
                    19.990
                  </span>
                  <span className="text-base font-bold text-navy mt-auto mb-1.5" data-id="element-141">
                    ARS
                  </span>
                </div>
                <span className="text-xs text-green-700 font-semibold mt-1 bg-green-50 px-2 py-0.5 rounded-full" data-id="element-142">
                  Ahorrás $30.000
                </span>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-5 mb-5 border border-slate-100" data-id="element-143">
              <p className="font-bold text-slate-800 mb-3 text-sm" data-id="element-144">Incluye:</p>
              <ul className="space-y-2.5" data-id="element-145">
                <li className="flex items-start gap-2.5" data-id="element-146">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-147" />
                  <span className="text-slate-700 font-medium text-sm" data-id="element-148">
                    Todo el KIT Psicología del Insomnio
                  </span>
                </li>
                <li className="flex items-start gap-2.5" data-id="element-149">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-150" />
                  <span className="text-slate-700 text-sm" data-id="element-151">
                    Sistema Psicología del Insomnio™ en 6 etapas
                  </span>
                </li>
                <li className="flex items-start gap-2.5" data-id="element-152">
                  <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" data-id="element-153" />
                  <span className="text-slate-800 font-semibold text-sm" data-id="element-154">
                    BONUS: Protocolo de Psicoeducación sobre el Sueño
                  </span>
                </li>
                <li className="flex items-start gap-2.5" data-id="element-155">
                  <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" data-id="element-156" />
                  <span className="text-slate-800 font-semibold text-sm" data-id="element-157">
                    BONUS: Diario de Sueño del Paciente
                  </span>
                </li>
                <li className="flex items-start gap-2.5" data-id="element-158">
                  <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" data-id="element-159" />
                  <span className="text-slate-800 font-semibold text-sm" data-id="element-160">
                    BONUS: Guía de Higiene del Sueño
                  </span>
                </li>
              </ul>
            </div>

            <CtaButton href="https://checkout.sectordigital.online/cart/44147795132459:1" className="w-full text-lg md:text-xl py-4 mb-4 shadow-[0_8px_30px_rgb(201,169,97,0.4)]" data-id="element-161">
              SÍ, QUIERO EL KIT AHORA
            </CtaButton>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-3 text-center border-t border-slate-100 pt-4" data-id="element-162">
              <div className="flex flex-col items-center gap-1.5" data-id="element-163">
                <CreditCard className="w-4 h-4 text-slate-400" data-id="element-164" />
                <span className="text-[11px] text-slate-500 font-medium" data-id="element-165">
                  Pago único
                </span>
              </div>
              <div className="flex flex-col items-center gap-1.5" data-id="element-166">
                <Clock className="w-4 h-4 text-slate-400" data-id="element-167" />
                <span className="text-[11px] text-slate-500 font-medium" data-id="element-168">
                  Acceso inmediato
                </span>
              </div>
              <div className="flex flex-col items-center gap-1.5" data-id="element-169">
                <ShieldCheck className="w-4 h-4 text-slate-400" data-id="element-170" />
                <span className="text-[11px] text-slate-500 font-medium" data-id="element-171">
                  Garantía 7 días
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}